/**
 * Chargement des couches de sites (`couches.sites`), partagé par le champ et la
 * carte : chaque fichier n'est téléchargé qu'une fois, à la première demande.
 *
 * Le champ en a besoin dès qu'une maille est affichée (formulaire de modification) :
 * `maille.sites` ne cite que les types des couches chargées. Sinon, les couches ne
 * sont lues qu'à l'ouverture de la carte.
 */
import type Feature from 'ol/Feature'
import type Geometry from 'ol/geom/Geometry'
import GeoJSON from 'ol/format/GeoJSON'

import { lireJson } from './donnees'
import { normaliserType, type CoucheSites, type FiltreSites } from './filtreSites'

/** Propriété posée sur chaque site chargé : son type, déclaré par la couche ou lu dans le site. */
export const PROP_TYPE = '_sgiType'

export interface SitesCharges {
  features: Feature<Geometry>[]
  /** Types des couches chargées, normalisés (majuscules) : le filtre de `maille.sites`. */
  filtre: ReadonlySet<string>
}

export class ChargeurSites {
  private promesse: Promise<SitesCharges> | null = null
  private readonly format = new GeoJSON({ dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })

  constructor(
    private readonly couches: CoucheSites[],
    /** Ajoute la clé aux URL du domaine de la grille, et à elles seules. */
    private readonly adresse: (url: string) => string,
    /** Propriété lue quand la couche ne déclare pas de type (`champs.siteType`). */
    private readonly champType: string | null,
    private readonly surErreur: (e: Error) => void,
  ) {}

  charger(): Promise<SitesCharges> {
    this.promesse ??= this.lire()
    return this.promesse
  }

  async filtre(): Promise<FiltreSites> {
    return (await this.charger()).filtre
  }

  private async lire(): Promise<SitesCharges> {
    const lues = await Promise.all(this.couches.map((c) => this.lireCouche(c)))
    const features = lues.flat()
    const filtre = new Set<string>()
    // Une couche déclarée compte même illisible : son type reste filtré comme prévu.
    for (const c of this.couches) if (c.type) filtre.add(normaliserType(c.type))
    for (const f of features) {
      const t = normaliserType(f.get(PROP_TYPE))
      if (t) filtre.add(t)
    }
    return { features, filtre }
  }

  /** Une couche illisible est signalée, et n'empêche pas les autres de s'afficher. */
  private async lireCouche(c: CoucheSites): Promise<Feature<Geometry>[]> {
    try {
      const fc = await lireJson<object>(this.adresse(new URL(c.url, document.baseURI).href))
      const features = this.format.readFeatures(fc) as Feature<Geometry>[]
      for (const f of features) {
        const lu = this.champType ? f.get(this.champType) : (f.get('type_site') ?? f.get('type'))
        f.set(PROP_TYPE, c.type ?? (lu == null ? '' : String(lu)), true)
      }
      return features
    } catch (e) {
      this.surErreur(e as Error)
      return []
    }
  }
}
